import clsx from 'clsx'
import { IamApi, type IamUser, type OrganizationRef } from 'api/IamApi'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import Title from 'components/Title/Title'
import UserAvatar from 'components/UserAvatar/UserAvatar'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useFitPageSize } from 'hooks/useFitPageSize'
import { useUser } from 'hooks/user'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import mapPageFilterDropdown from 'styles/mapPageFilterDropdown.module.css'
import { PATH, PROGRESS_TYPES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { ALL_AUDIENCES, assignableAudiences } from 'utils/iamAudiences'
import { enumParam, iamUserParam, optionalIntParam, pageParam, stringParam } from 'utils/urlQueryState'

import IamUserEditor, { type IamFacilityOption, type IamRobotOption } from './IamUserEditor'
import style from './AccountUsers.module.css'

const STATUS_VALUES = ['all', 'active', 'inactive'] as const
const AUDIENCE_FILTER_VALUES = ['all', ...ALL_AUDIENCES] as const

const AccountUsers = () => {
  const { t } = useTranslation()
  const user = useUser()
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    status: enumParam(STATUS_VALUES, 'all'),
    audience: enumParam(AUDIENCE_FILTER_VALUES, 'all'),
    organization: stringParam(),
    facility: optionalIntParam(),
    page: pageParam(),
    user: iamUserParam(),
  })
  const [progress, setProgress] = useState(PROGRESS_TYPES.IDLE)
  const [rows, setRows] = useState<IamUser[]>([])
  const [total, setTotal] = useState(0)
  const [facilities, setFacilities] = useState<IamFacilityOption[]>([])
  const [facilityNames, setFacilityNames] = useState<Record<number, string>>({})
  const [robots, setRobots] = useState<IamRobotOption[]>([])
  const [robotNames, setRobotNames] = useState<Record<number, string>>({})
  const [organizations, setOrganizations] = useState<OrganizationRef[]>([])
  const [createdPassword, setCreatedPassword] = useState<string | null>(null)
  const [attachedExisting, setAttachedExisting] = useState(false)
  const [copied, setCopied] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableBodyRef = useRef<HTMLDivElement>(null)
  const isListView = url.user == null
  const pageSize = useFitPageSize(tableBodyRef, {
    enabled: isListView,
    page: url.page,
    layoutKey: rows.length,
    onPageChange: nextPage => setUrl({ page: nextPage }),
  })

  const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)))
  const canSeeInactiveUsers = user?.state.audience === 'developer'
  const canAssignInternalServiceman = Boolean(user?.state.organization?.can_assign_internal_serviceman)

  useEffect(() => {
    if (!canSeeInactiveUsers && url.status === 'inactive') {
      setUrl({ status: 'all', page: 1 })
    }
  }, [canSeeInactiveUsers, setUrl, url.status])

  useEffect(() => {
    if (!canAssignInternalServiceman && url.audience === 'internal_serviceman') {
      setUrl({ audience: 'all', page: 1 })
    }
  }, [canAssignInternalServiceman, setUrl, url.audience])

  const isActiveFilter = url.status === 'all' ? null : url.status === 'active'

  const fetchUsers = useCallback(async () => {
    if (!isListView || pageSize < 1) return
    setProgress(PROGRESS_TYPES.WORK)
    try {
      const response = await IamApi.listUsers({
        page: url.page,
        pageSize,
        search: url.q || undefined,
        isActive: isActiveFilter,
        audience: url.audience === 'all' ? undefined : url.audience,
        organization: url.organization || undefined,
        facilityId: url.facility,
      })
      setRows(response.results)
      setTotal(response.total)
      setProgress(PROGRESS_TYPES.SUCCESS)
    } catch (error) {
      console.error('Error fetching IAM users:', error)
      setProgress(PROGRESS_TYPES.ERROR)
    }
  }, [isActiveFilter, isListView, pageSize, url.audience, url.facility, url.organization, url.page, url.q])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    if (pageSize < 1 || total === 0) return
    if (url.page > totalPages) setUrl({ page: totalPages })
  }, [pageSize, setUrl, total, totalPages, url.page])

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const response = await MainApi.fetchFacilities(1, 1000)
        const options = (response.results || []).map((item: { id: number; name: string }) => ({
          id: item.id,
          name: item.name,
        }))
        setFacilities(options)
        setFacilityNames(Object.fromEntries(options.map(item => [item.id, item.name])))
      } catch (error) {
        console.error('Error fetching facilities for IAM:', error)
      }
    }
    loadFacilities()
  }, [])

  useEffect(() => {
    const loadRobots = async () => {
      try {
        const response = await RobotApi.fetchRobots(1, 1000, undefined, undefined, undefined, undefined, true)
        const options = (response.results || []).map(item => ({
          id: item.id,
          name: item.name || item.serialNumber,
        }))
        setRobots(options)
        setRobotNames(Object.fromEntries(options.map(item => [item.id, item.name])))
      } catch (error) {
        console.error('Error fetching robots for IAM:', error)
      }
    }
    loadRobots()
  }, [])

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response = await IamApi.listFilterOptions()
        setOrganizations(response.organizations)
      } catch (error) {
        console.error('Error fetching IAM organizations:', error)
      }
    }
    loadOrganizations()
  }, [])

  const onSearchChange = (value: string) => {
    setUrl({ q: value })
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setUrl({ q: value, page: 1 })
    }, 300)
  }

  const facilityLabel = useCallback(
    (ids: number[]) => {
      const names = ids.map(id => facilityNames[id]).filter(Boolean)
      if (!names.length) return t('account.users_no_assigned_facilities')
      if (names.length <= 2) return names.join(', ')
      return t('account.users_facilities_more', { first: names[0], count: names.length - 1 })
    },
    [facilityNames, t]
  )

  const robotLabel = useCallback(
    (ids: number[] | null) => {
      if (ids == null) return t('account.users_robots_all')
      const names = ids.map(id => robotNames[id]).filter(Boolean)
      if (!names.length) {
        return ids.length ? t('account.users_robots_count', { count: ids.length }) : t('account.users_no_assigned_robots')
      }
      if (names.length <= 2) return names.join(', ')
      return t('account.users_robots_more', { first: names[0], count: names.length - 1 })
    },
    [robotNames, t]
  )

  const displayName = (row: IamUser) =>
    [row.first_name, row.last_name].filter(Boolean).join(' ') || row.email

  const statusOptions: IOption[] = useMemo(() => {
    const values = canSeeInactiveUsers ? STATUS_VALUES : (['all', 'active'] as const)
    return values.map(value => ({
      value,
      text: t(
        value === 'all'
          ? 'account.users_filter_all'
          : value === 'active'
          ? 'account.users_filter_active'
          : 'account.users_filter_inactive'
      ),
    }))
  }, [canSeeInactiveUsers, t])
  const currentStatusOption = statusOptions.find(option => option.value === url.status)

  const audienceOptions: IOption[] = useMemo(
    () => [
      { value: 'all', text: t('account.users_filter_all_roles') },
      ...assignableAudiences(user?.state.audience ?? null, {
        canAssignInternalServiceman,
      }).map(value => ({
        value,
        text: t(`account.audience_${value}`),
      })),
    ],
    [canAssignInternalServiceman, t, user?.state.audience]
  )
  const currentAudienceOption = audienceOptions.find(option => option.value === url.audience)

  const facilityOptions: IOption[] = useMemo(
    () => [
      { value: '', text: t('account.users_filter_all_facilities') },
      ...facilities.map(item => ({ value: String(item.id), text: item.name })),
    ],
    [facilities, t]
  )
  const currentFacilityOption =
    facilityOptions.find(option => option.value === (url.facility == null ? '' : String(url.facility))) ||
    facilityOptions[0]

  const organizationOptions: IOption[] = useMemo(() => {
    const selectedId = url.organization
    const knownIds = new Set(organizations.map(org => String(org.id)))
    const options = organizations.map(org => ({ value: String(org.id), text: org.name }))
    if (selectedId && !knownIds.has(selectedId)) {
      options.unshift({ value: selectedId, text: selectedId })
    }
    return [
      { value: '', text: t('account.users_filter_all_organizations') },
      ...options,
    ]
  }, [organizations, t, url.organization])
  const currentOrganizationOption =
    organizationOptions.find(option => option.value === url.organization) || organizationOptions[0]

  const onEditorSaved = (result: { user: IamUser; created: boolean; attached?: boolean; password?: string }) => {
    setUrl({ user: undefined })
    if (result.created && result.password) {
      setCreatedPassword(result.password)
      setAttachedExisting(false)
    } else if (result.attached) {
      setAttachedExisting(true)
      setCreatedPassword(null)
    }
    fetchUsers()
    IamApi.listFilterOptions()
      .then(response => setOrganizations(response.organizations))
      .catch(() => undefined)
  }

  const copyPassword = async () => {
    if (!createdPassword) return
    try {
      await navigator.clipboard.writeText(createdPassword)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  if (!user?.state.canUseIam) {
    return <Navigate to={PATH.ACCOUNT_PROFILE} replace />
  }

  const passwordDialog = (
    <ConfirmDialog
      isOpen={Boolean(createdPassword) || attachedExisting}
      onClose={() => {
        setCreatedPassword(null)
        setAttachedExisting(false)
        setCopied(false)
      }}
      onConfirmClick={() => {
        setCreatedPassword(null)
        setAttachedExisting(false)
        setCopied(false)
      }}
      title={attachedExisting ? t('account.users_attached_title') : t('account.users_created_title')}
      confirmText={t('common.ok')}
      showCancelButton={false}
    >
      {attachedExisting ? (
        t('account.users_attached_body')
      ) : (
        <>
          {t('account.users_created_body')}
          <div className={style.passwordBox}>
            <span data-test-id={getTestId('iam-created-password')}>{createdPassword}</span>
            <Button.Outlined onClick={copyPassword}>
              {copied ? t('account.users_copied') : t('common.copy')}
            </Button.Outlined>
          </div>
        </>
      )}
    </ConfirmDialog>
  )

  const isCreating = url.user === 'new'
  const editorUserId = typeof url.user === 'number' ? url.user : undefined

  if (isCreating || editorUserId) {
    return (
      <>
        <IamUserEditor
          isOpen
          mode={isCreating ? 'create' : 'edit'}
          userId={editorUserId}
          actorAudience={user.state.audience}
          actorCanAssignInternalServiceman={Boolean(user.state.organization?.can_assign_internal_serviceman)}
          facilities={facilities}
          robots={robots}
          onClose={() => setUrl({ user: undefined })}
          onSaved={onEditorSaved}
        />
        {passwordDialog}
      </>
    )
  }

  return (
    <div className={style.panel} data-test-id={getTestId('account-users')}>
      <div className={style.headerArea}>
        <div className={style.titleBlock}>
          <div>
            <Title text={t('account.users_title')} />
            <div className={style.subtitle}>{t('account.users_subtitle')}</div>
          </div>
          <div className={style.headerRight}>
            <Button.Standart
              onClick={() => setUrl({ user: 'new' })}
              dataTestId="iam-create"
            >
              {t('account.users_create')}
            </Button.Standart>
          </div>
        </div>
        <div className={style.controlsBlock}>
          <input
            className={style.searchInput}
            value={url.q}
            onChange={event => onSearchChange(event.target.value)}
            placeholder={t('common.search')}
            data-test-id={getTestId('iam-search')}
          />
          <Dropdown
            className={mapPageFilterDropdown.filterDropdown}
            options={organizationOptions}
            currentOption={currentOrganizationOption}
            onClickOption={option => setUrl({ organization: String(option.value || ''), page: 1 })}
            dataTestId="iam-organization-filter"
            menuFitContent
          />
          <Dropdown
            className={mapPageFilterDropdown.filterDropdown}
            options={audienceOptions}
            currentOption={currentAudienceOption}
            onClickOption={option =>
              setUrl({ audience: option.value as (typeof AUDIENCE_FILTER_VALUES)[number], page: 1 })
            }
            dataTestId="iam-audience-filter"
            menuFitContent
          />
          <Dropdown
            className={mapPageFilterDropdown.filterDropdownNarrow}
            options={statusOptions}
            currentOption={currentStatusOption}
            onClickOption={option =>
              setUrl({ status: option.value as (typeof STATUS_VALUES)[number], page: 1 })
            }
            dataTestId="iam-status-filter"
          />
          <Dropdown
            className={mapPageFilterDropdown.filterDropdown}
            options={facilityOptions}
            currentOption={currentFacilityOption}
            onClickOption={option => {
              const value = option.value
              setUrl({
                facility: value === '' || value == null ? undefined : Number(value),
                page: 1,
              })
            }}
            dataTestId="iam-facility-filter"
            menuFitContent
          />
        </div>
      </div>

      {progress === PROGRESS_TYPES.ERROR ? (
        <div className={style.errorText}>{t('account.users_load_error')}</div>
      ) : null}
      <div className={style.tableContainer}>
        <div className={style.tableWrapper}>
          <div className={style.tableHeader}>
            <div>{t('account.users_col_name')}</div>
            <div>{t('account.email_label')}</div>
            <div className={style.hideMobile}>{t('account.users_organization')}</div>
            <div className={style.hideMobile}>{t('account.users_role')}</div>
            <div>{t('account.users_status')}</div>
            <div className={style.hideMobile}>{t('account.users_facilities')}</div>
            <div className={style.hideMobile}>{t('account.users_robots')}</div>
          </div>
          <div className={style.table} ref={tableBodyRef}>
            {progress === PROGRESS_TYPES.WORK && !rows.length ? (
              <Loader />
            ) : rows.length ? (
              rows.map(row => (
                <button
                  type="button"
                  key={row.id}
                  className={clsx(style.tableRow, !row.is_active && style.tableRowInactive)}
                  data-fit-page-row=""
                  onClick={() => setUrl({ user: row.id })}
                  data-test-id={getTestId(`iam-row-${row.id}`)}
                >
                  <div className={style.nameCell}>
                    <UserAvatar
                      url={row.avatar_url}
                      firstName={row.first_name}
                      lastName={row.last_name}
                      email={row.email}
                      size={28}
                    />
                    <div className={style.cell}>{displayName(row)}</div>
                  </div>
                  <div className={style.cell}>{row.email}</div>
                  <div className={`${style.cell} ${style.hideMobile}`}>{row.organization?.name || ''}</div>
                  <div className={`${style.cell} ${style.hideMobile}`}>{t(`account.audience_${row.audience}`)}</div>
                  <div>
                    <span className={clsx(style.statusBadge, row.is_active ? style.statusActive : style.statusInactive)}>
                      {row.is_active ? t('account.users_filter_active') : t('account.users_filter_inactive')}
                    </span>
                  </div>
                  <div className={`${style.cell} ${style.hideMobile}`}>{facilityLabel(row.facility_ids)}</div>
                  <div className={`${style.cell} ${style.hideMobile}`}>{robotLabel(row.visible_robot_ids)}</div>
                </button>
              ))
            ) : progress === PROGRESS_TYPES.SUCCESS ? (
              <div className={style.empty}>{t('account.users_empty')}</div>
            ) : null}
          </div>
        </div>
        {total > pageSize && pageSize > 0 ? (
          <TablePagination
            page={url.page}
            totalPages={totalPages}
            pageInput={String(url.page)}
            onPageInputChange={value => {
              const page = parseInt(value, 10)
              if (!Number.isNaN(page) && page >= 1 && page <= totalPages) setUrl({ page })
            }}
            onPageInputKeyDown={() => undefined}
            onPageInputBlur={() => undefined}
            onPrev={() => setUrl({ page: Math.max(1, url.page - 1) })}
            onNext={() => setUrl({ page: Math.min(totalPages, url.page + 1) })}
          />
        ) : null}
      </div>

      {passwordDialog}
    </div>
  )
}

export default AccountUsers
